/**
 * dataGridRangeKeyboardHelpers
 *
 * Pure helper functions for keyboard-driven range selection.
 * Extracted from useDataGridKeyboard to keep file sizes manageable.
 *
 * Plan 124, Epic 03 — Shift+Arrow Keyboard Extension
 */

import type { CellPosition, CellRange } from './types';

// ---------------------------------------------------------------------------
// Multi-cell range detection
// ---------------------------------------------------------------------------

/**
 * True when range spans more than one cell.
 * CRITICAL: `selectionRange` is non-null even for collapsed single-cell
 * selections (anchor === focus). This helper checks the actual positions.
 */
export function isMultiCellRange(range: CellRange | null): range is CellRange {
  if (!range) return false;
  return (
    range.anchor.rowIndex !== range.focus.rowIndex ||
    range.anchor.columnId !== range.focus.columnId
  );
}

// ---------------------------------------------------------------------------
// Visible column navigation (includes readonly)
// ---------------------------------------------------------------------------

/** Get next visible column ID (wraps NOT — returns null at end) */
export function getNextVisibleCol(
  colId: string,
  visibleColumnIds: string[],
): string | null {
  const idx = visibleColumnIds.indexOf(colId);
  if (idx < 0 || idx >= visibleColumnIds.length - 1) return null;
  return visibleColumnIds[idx + 1];
}

/** Get previous visible column ID (returns null at start) */
export function getPrevVisibleCol(
  colId: string,
  visibleColumnIds: string[],
): string | null {
  const idx = visibleColumnIds.indexOf(colId);
  if (idx <= 0) return null;
  return visibleColumnIds[idx - 1];
}

// ---------------------------------------------------------------------------
// Tab cycling within range (editable cells only)
// ---------------------------------------------------------------------------

/**
 * Get next editable cell within range bounds for Tab cycling.
 * Iterates left-to-right, top-to-bottom through editable cells in the range.
 * Wraps around at boundaries.
 */
export function getNextEditableCellInRange(
  from: CellPosition,
  editableColumnIds: string[],
  minRow: number,
  maxRow: number,
  minColIdx: number,
  maxColIdx: number,
  visibleColumnIds: string[],
  reverse: boolean,
): CellPosition | null {
  // Build ordered list of editable cells within range
  const cells: CellPosition[] = [];
  for (let r = minRow; r <= maxRow; r++) {
    for (let c = minColIdx; c <= maxColIdx; c++) {
      const colId = visibleColumnIds[c];
      if (colId && editableColumnIds.includes(colId)) {
        cells.push({ rowIndex: r, columnId: colId });
      }
    }
  }
  if (cells.length === 0) return null;

  // Find current position in cycle
  const currentIdx = cells.findIndex(
    (c) => c.rowIndex === from.rowIndex && c.columnId === from.columnId,
  );

  if (reverse) {
    const nextIdx = currentIdx <= 0 ? cells.length - 1 : currentIdx - 1;
    return cells[nextIdx];
  }
  const nextIdx = currentIdx >= cells.length - 1 ? 0 : currentIdx + 1;
  return cells[nextIdx];
}

// ---------------------------------------------------------------------------
// Enter cycling within range (move down in column, wrap to next column)
// ---------------------------------------------------------------------------

/**
 * Get next cell moving down within the same column inside range bounds.
 * At the bottom, wraps to the top of the next editable column in range.
 * At the very last position, wraps to the first position.
 */
export function getNextCellInColumnInRange(
  from: CellPosition,
  editableColumnIds: string[],
  minRow: number,
  maxRow: number,
  minColIdx: number,
  maxColIdx: number,
  visibleColumnIds: string[],
): CellPosition | null {
  const fromColIdx = visibleColumnIds.indexOf(from.columnId);

  // Try next row in same column
  if (from.rowIndex < maxRow) {
    return { rowIndex: from.rowIndex + 1, columnId: from.columnId };
  }

  // At bottom of column — find next editable column in range
  for (let c = fromColIdx + 1; c <= maxColIdx; c++) {
    const colId = visibleColumnIds[c];
    if (colId && editableColumnIds.includes(colId)) {
      return { rowIndex: minRow, columnId: colId };
    }
  }

  // Wrap to first editable column in range
  for (let c = minColIdx; c <= maxColIdx; c++) {
    const colId = visibleColumnIds[c];
    if (colId && editableColumnIds.includes(colId)) {
      return { rowIndex: minRow, columnId: colId };
    }
  }

  return null;
}
